import { Disclosure } from '@headlessui/react';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import Script from 'next/script';
import { useCallback, useEffect, useState } from 'react';
import { FAQPage, Question, WithContext } from 'schema-dts';

export interface AccordionItem {
  label: string;
  body: string;
  labelDaAnalytics?: string;
  bodyDataAnalytics?: string;
}

export interface AccordionProps {
  items: AccordionItem[];
  schemaType?: string;
  sectionId?: string;
}

const Accordion: React.FC<AccordionProps> = ({ items, schemaType, sectionId }) => {
  // Json-ld schema.
  const [jsonLd, setJsonLd] = useState<WithContext<FAQPage>>();
  const jsonLdId = `jsonld-accordion-${Math.floor(Math.random() * 1000)}`;

  const setFAQPageSchema = useCallback((items: AccordionItem[]) => {
    const schemaItems: Array<Question> = [];
    const schemaData: WithContext<FAQPage> = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
    };
    items.forEach((item) => {
      schemaItems.push({
        '@type': 'Question',
        name: item.label,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.body,
        },
      });
    });
    if (schemaItems) {
      schemaData.mainEntity = schemaItems;
      setJsonLd(schemaData as WithContext<FAQPage>);
    }
  }, []);

  useEffect(() => {
    if (schemaType) {
      switch (schemaType) {
        case 'FAQPage':
          setFAQPageSchema(items);
          break;
        default:
          break;
      }
    }
  }, [items, schemaType, setFAQPageSchema]);

  return (
    <>
      {jsonLd && (
        <Script
          id={jsonLdId}
          type="application/ld+json"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <div data-id="accordion-container" className="mx-auto max-w-7xl px-24 lg:px-32" id={sectionId}>
        <div data-id="accordion-wrapper" className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
          <dl data-id="accordion-list" className="mt-24 divide-y divide-gray-900/10 border-t border-gray-900/10">
            {items.map((item, index) => (
              <Disclosure as="div" key={item.label} data-id={`accordion-item-${index}`} className="">
                {({ open }) => (
                  <>
                    <dt data-id={`accordion-question-${index}`}>
                      <Disclosure.Button
                        data-id={`accordion-button-${index}`}
                        className="flex w-full items-center justify-between py-8 text-left text-gray-900"
                        padi-analytics={item.labelDaAnalytics}
                      >
                        <span data-id={`accordion-label-${index}`} className="text-base font-medium leading-6">
                          {item.label}
                        </span>
                        {open ? (
                          <span
                            data-id={`accordion-icon-open-${index}`}
                            className="material-symbols-outlined block text-4xl"
                            aria-hidden="true"
                          >
                            expand_less
                          </span>
                        ) : (
                          <span
                            data-id={`accordion-icon-closed-${index}`}
                            className="material-symbols-outlined block text-4xl"
                            aria-hidden="true"
                          >
                            expand_more
                          </span>
                        )}
                      </Disclosure.Button>
                    </dt>
                    <Disclosure.Panel
                      as="dd"
                      data-id={`accordion-panel-${index}`}
                      className="prose max-w-none pb-14 pr-12 text-padi-gray-darker"
                      padi-analytics={item.bodyDataAnalytics}
                    >
                      {isStringHtml(item.body) ? (
                        <div data-id={`accordion-content-${index}`} dangerouslySetInnerHTML={{ __html: item.body }} />
                      ) : (
                        <Markdown
                          data-id={`accordion-content-${index}`}
                          className="text-base leading-6"
                          markdown={item.body}
                        />
                      )}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </>
  );
};

export default Accordion;
